import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
  secondaryAction?: {
    label: string;
    href: string;
  };
  className?: string;
  variant?: "default" | "compact";
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  className = "",
  variant = "default",
}: EmptyStateProps) {
  const isCompact = variant === "compact";
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center animate-fade-in",
        isCompact ? "py-10 px-4" : "py-16 px-6",
        className
      )}
    >
      <div
        className={cn(
          "rounded-2xl flex items-center justify-center mb-5",
          isCompact ? "w-12 h-12" : "w-16 h-16",
        )}
        style={{
          background: "linear-gradient(135deg, hsl(var(--muted)) 0%, hsl(var(--muted) / 0.5) 100%)",
          boxShadow: "inset 0 1px 0 hsl(0 0% 100% / 0.06), 0 1px 4px hsl(0 0% 0% / 0.06)",
        }}
      >
        <Icon
          className={cn(
            "text-muted-foreground/50",
            isCompact ? "h-5 w-5" : "h-7 w-7"
          )}
          strokeWidth={1.5}
        />
      </div>
      <h3
        className={cn(
          "font-semibold text-foreground mb-1.5",
          isCompact ? "text-sm" : "text-base"
        )}
      >
        {title}
      </h3>
      <p
        className={cn(
          "text-muted-foreground leading-relaxed",
          isCompact ? "text-xs max-w-xs" : "text-sm max-w-sm",
          "mb-6"
        )}
      >
        {description}
      </p>
      {(action || secondaryAction) && (
        <div className="flex flex-col sm:flex-row items-center gap-2">
          {action && (
            action.href ? (
              <Button asChild size={isCompact ? "sm" : "default"}>
                <Link href={action.href}>{action.label}</Link>
              </Button>
            ) : (
              <Button size={isCompact ? "sm" : "default"} onClick={action.onClick}>
                {action.label}
              </Button>
            )
          )}
          {secondaryAction && (
            <Button asChild variant="outline" size={isCompact ? "sm" : "default"}>
              <Link href={secondaryAction.href}>{secondaryAction.label}</Link>
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
